/**
 * The shortlist, shared between the header count and the catalogue page.
 *
 * There are "add to shortlist" buttons in four places - search results,
 * category cards, the reverse-lookup results and the classification tree - and
 * a live count in the header. Without one shared store those five surfaces
 * drift the moment anything is added, so the whole basket lives here and every
 * mutation refreshes it from the server response rather than guessing locally.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { shortlistApi } from './api.js';
import { useAuth } from './auth-context.jsx';

const ShortlistContext = createContext(null);

export function ShortlistProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Codes with a request in flight, so a button can disable itself without
  // freezing the rest of the list.
  const [pending, setPending] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    setIsLoading(true);
    try {
      const data = await shortlistApi.list();
      setItems(data.items ?? []);
      setError(null);
    } catch (caught) {
      setError(caught.message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markPending = useCallback((codes, active) => {
    setPending((current) => {
      const next = new Set(current);
      for (const code of codes) {
        if (active) next.add(code);
        else next.delete(code);
      }
      return next;
    });
  }, []);

  const add = useCallback(
    async (codes, options = {}) => {
      const list = Array.isArray(codes) ? codes : [codes];
      if (list.length === 0) return null;
      markPending(list, true);
      try {
        const result = await shortlistApi.add(list, options);
        await refresh();
        setError(null);
        return result;
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending(list, false);
      }
    },
    [markPending, refresh],
  );

  const remove = useCallback(
    async (code) => {
      markPending([code], true);
      try {
        await shortlistApi.remove(code);
        await refresh();
        setError(null);
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending([code], false);
      }
    },
    [markPending, refresh],
  );

  const clear = useCallback(async () => {
    await shortlistApi.clear();
    await refresh();
  }, [refresh]);

  const codes = useMemo(() => new Set(items.map((item) => item.sicCode)), [items]);

  const value = useMemo(
    () => ({
      items,
      codes,
      count: items.length,
      isLoading,
      error,
      pending,
      has: (code) => codes.has(code),
      isPending: (code) => pending.has(code),
      add,
      remove,
      clear,
      refresh,
    }),
    [items, codes, isLoading, error, pending, add, remove, clear, refresh],
  );

  return <ShortlistContext.Provider value={value}>{children}</ShortlistContext.Provider>;
}

export function useShortlist() {
  const context = useContext(ShortlistContext);
  if (!context) throw new Error('useShortlist must be used inside a ShortlistProvider.');
  return context;
}
